import moment from 'moment'
import * as React from 'react'
import { useEffect } from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import { PageWrapper } from '../../components/atoms/PageWrapper'
import { SectionTitle } from '../../components/atoms/SectionTittle'
import { ConfirmDialog } from '../../components/molecules/ConfirmDialog'
import { GameLoserView } from '../../components/molecules/GameLoserView'
import { GameWinnerView } from '../../components/molecules/GameWinnerView'
import { GameGridPuzzleView } from '../../components/organisms/GameGridPuzzleView'
import { GameSimplePuzzleView } from '../../components/organisms/GameSimplePuzzleView'
import { cancelGame, getStartedGame } from '../../services/game-services'
import {
    invoke, makeLocalUri,
    useHistoryBackOverride
} from '../../utils/helper'
import { StartedGameDto } from '../../utils/types'

export const Game: React.FC = () => {
    const history = useHistory()
    const { state }: { state: { game: StartedGameDto } | undefined } = useLocation()

    const [game, setGame] = React.useState<StartedGameDto | undefined>(() => {
        const g = state?.game
        if (g && !g.finishedAt && g.puzzles.every((pz) => pz.finishedAt)) {
            g.finishedAt = g.puzzles[g.puzzles.length - 1].finishedAt
        }

        return g
    })

    if (game && !window.location.pathname.includes(game.gameId)) {
        window.history.replaceState({}, document.title, makeLocalUri(`/spil/${game.gameId}`))
    }

    const calculateSecondsLeft = () => {
        if (!game) {
            return 0
        }

        return Math.max(0, moment.duration(
            moment.utc(game.deadlineAt).diff(moment.utc())
        ).asSeconds())
    }

    const [puzzle, setPuzzle] = React.useState<number>(-1)
    const [secondsLeft, setSecondsLeft] = React.useState<number>(0)
    const [askingToCancel, setAskingToCancel] = React.useState(false)

    const cancel = async () => {
        if (game) {
            await invoke(async () => {
                await cancelGame(game.gameId)
            }, {
                operation: 'Annullerer'
            })
        }

        history.push('/')
    }

    useHistoryBackOverride('Er du sikker på, at du vil afslutte?', cancel)

    useEffect(() => {
        if (game && !game.finishedAt) {
            setSecondsLeft(calculateSecondsLeft())
            setPuzzle(game.puzzles.findIndex((pz) => !pz.finishedAt))

            const intervalId = setInterval(() => {
                const newSecondsLeft = calculateSecondsLeft()
                setSecondsLeft(newSecondsLeft)
                
                // Stop timer if game is finished or time is up
                if (game.finishedAt || newSecondsLeft <= 0) {
                    clearInterval(intervalId)
                }
            }, 1000)

            // eslint-disable-next-line consistent-return
            return () => clearInterval(intervalId)
        } else {
            return undefined
        }
    }, [game])

    if (!game) {
        return <Redirect to="/"/>
    }

    const nextPuzzle = async (puzzleIndex: number) => {
        if (puzzleIndex >= 0 && puzzleIndex < game.puzzles.length) {
            setPuzzle(puzzleIndex)
        } else {
            await invoke(async () => {
                setGame(
                    await getStartedGame(game.gameId)
                )
            })
        }
    }

    const showPuzzleView = !game.finishedAt && secondsLeft > 0 && puzzle >= 0 && puzzle < game.puzzles.length
    const showWonView = !!game.finishedAt
    const showLostView = !game.finishedAt && secondsLeft <= 0

    const title = showPuzzleView ? game.escapeRoom.puzzles[puzzle].puzzleId : undefined

    return (
        <PageWrapper>
            {askingToCancel && (
                <ConfirmDialog
                    title="Afslut spil?"
                    description="Er du sikker på, at du vil afslutte?"
                    onCancel={() => setAskingToCancel(false)}
                    onConfirm={cancel}/>
            )}
            {showPuzzleView && (
                <>
                    <SectionTitle text={title ? `Opgaver - ${title}` : 'Spil'}/>
                    {game.escapeRoom.puzzles[puzzle].type === 'simple' && (
                        <GameSimplePuzzleView
                            secondsLeft={secondsLeft}
                            game={game}
                            puzzleIndex={puzzle}
                            onComplete={() => nextPuzzle(puzzle + 1)}
                            onReset={() => setAskingToCancel(true)}/>
                    )}
                    {game.escapeRoom.puzzles[puzzle].type === 'grid' && (
                        <GameGridPuzzleView
                            secondsLeft={secondsLeft}
                            game={game}
                            puzzleIndex={puzzle}
                            onComplete={() => nextPuzzle(puzzle + 1)}
                            onReset={() => setAskingToCancel(true)}/>
                    )}
                </>
            )}
            {showWonView && (
                <>
                    <SectionTitle text="Tillykke, du vandt!"/>
                    <GameWinnerView
                        game={game}
                        secondsLeft={secondsLeft}
                        onComplete={cancel}/>
                </>
            )}
            {showLostView && (
                <>
                    <SectionTitle text="Bedre held næste gang"/>
                    <GameLoserView
                        secondsLeft={secondsLeft}
                        game={game}
                        onComplete={cancel}/>
                </>
            )}
        </PageWrapper>
    )
}
